// The branded PowerPoint deck (Phase 5B) — built from the CURRENT filtered
// report state, driven BY THE METRICS REGISTRY and nothing else.
//
// ── R-022, restated for the deck ───────────────────────────────────────────
// Not one number on a slide is computed here. Every value, every table row and
// every headline arrives as a MetricResult from src/metrics/registry.js via
// data/reports.js aggregate() — the same call the screen makes and the same call
// the CSV makes. In v_20 exportReportPPT recomputed its own KPI list, which is
// how a funder deck ended up disagreeing with the page it was exported from.
// Here the deck is a RENDERING of the screen's numbers, never a second opinion.
//
// ── The split this file enforces ───────────────────────────────────────────
// buildSlidePlan() is PURE: registry results + filters + tile selection in, an
// array of slide DESCRIPTORS out. No DOM, no canvas, no pptxgenjs, no clock it
// did not receive. That is what makes the privacy rules testable — the leak
// assertion runs over the plan, in node, with no browser anywhere.
// renderDeck() is the impure half: it turns descriptors into pptxgenjs calls.
//
// ── PRIVACY (Kenya Data Protection Act 2019) ───────────────────────────────
// A deck is the single most-forwarded artefact this app produces: it goes to
// funders, ministries and boards, over email, unencrypted. So it carries
// AGGREGATED COUNTS ONLY. Three enforced rules:
//   1. NO PATIENT-LEVEL METRIC. Any registry metric marked exportable:false
//      (today: at_risk_patients, which lists named patients) is excluded from
//      every slide kind — it cannot be selected onto a deck even deliberately.
//   2. NO IDENTIFIER FIELDS. findIdentifierLeaks() walks the finished plan and
//      fails it if any descriptor carries an identifier-shaped key, or any cell
//      matches a real identifier value from the dataset it was built over.
//   3. SMALL-CELL SUPPRESSION. Geographic breakdowns hide any cell below
//      SMALL_CELL_MIN. "1 patient in Lamu County, on the Hepatitis C programme"
//      is not an aggregate — in a county of that size it is a person. The count
//      of suppressed rows is disclosed; the counts themselves are not.

import {
  METRICS,
  kpiMetrics,
  getMetric,
  metricDelta,
  fmtNum,
} from '../metrics/registry.js';

// ─── Brand tokens (the approved mock's palette, as pptx hex — no '#') ──────

export const PPT = Object.freeze({
  NAVY: '1F4477',
  INK: '1F2937',
  MUTED: '6B7280',
  LINE: 'E5E7EB',
  ALT: 'F5F8FC',
  WHITE: 'FFFFFF',
  ONNAVY: 'B9C6DA', // muted text ON the navy cover
  TILE: 'EEF3FA',
});

export const DECK_TITLE = 'Tumaini Haven — Programme Report';
export const DECK_SUBTITLE = 'Patient Support Programme · InsightsIQ';
export const DECK_FOOTER = 'Tumaini Haven PSP · confidential · aggregated data only';

/**
 * Cells below this count are suppressed in geographic breakdowns. 5 is the
 * conventional small-cell threshold in public-health reporting; it is a constant
 * here so the screen note, the slide note and the test all quote the same number.
 */
export const SMALL_CELL_MIN = 5;

/**
 * Row keys that identify a PERSON. No slide descriptor may carry any of these,
 * whatever the route that put it there. Checked structurally by
 * findIdentifierLeaks() so a future table slide built from raw case rows fails
 * the test suite rather than the Data Protection Act.
 */
export const IDENTIFIER_KEYS = Object.freeze([
  'case_id',
  'patient_name',
  'patient_phone',
  'phone',
  'reference_number',
  'national_id',
  'dob',
  'date_of_birth',
  'email',
  'patient_email',
  'next_of_kin',
  'next_of_kin_phone',
  'physical_address',
  'submission_id',
  'assigned_to',
  'created_by',
  'actor_user_id',
  'user_id',
]);

// ─── Pure helpers ──────────────────────────────────────────────────────────

/**
 * Registry metrics that render a chart AND may leave the page. Derived from the
 * registry's own `chart` flag — the deck never keeps its own chart list, which
 * is how the legacy deck ended up exporting a canvas the page had stopped
 * drawing.
 * @returns {any[]}
 */
export function deckChartMetrics() {
  return METRICS.filter((m) => !!m.chart && m.exportable !== false);
}

/**
 * Metrics whose registry result feeds a native table slide, in slide order.
 * Same exportable rule.
 * @returns {any[]}
 */
export function deckTableMetrics() {
  return ['programme_breakdown', 'closure_reasons', 'patients_by_county', 'field_completeness']
    .map((id) => getMetric(id))
    .filter((m) => m && m.exportable !== false);
}

/**
 * Suppress small cells in a ranked {l, v} list. Rows at or above `min` survive
 * unchanged; everything below collapses into ONE disclosure row carrying the
 * number of hidden categories and NO counts.
 *
 * Returns the visible rows plus a `note` sentence (null when nothing was hidden)
 * so the slide can state what it is not showing — suppression that is not
 * disclosed reads as "there is nothing there", which is its own kind of lie.
 *
 * @param {Array<{l: string, v: number}>} items
 * @param {number} [min]
 * @returns {{items: Array<{l: string, v: number}>, hidden: number, note: string|null}}
 */
export function suppressSmallCells(items, min) {
  const floor = typeof min === 'number' ? min : SMALL_CELL_MIN;
  const list = Array.isArray(items) ? items : [];
  const kept = [];
  let hidden = 0;
  for (const it of list) {
    const v = Number(it && it.v);
    if (isFinite(v) && v >= floor) kept.push({ l: String((it && it.l) || '—'), v });
    else hidden++;
  }
  return {
    items: kept,
    hidden,
    note: hidden
      ? `${fmtNum(hidden)} ${hidden === 1 ? 'category' : 'categories'} with fewer than ${floor} patients ${hidden === 1 ? 'is' : 'are'} withheld (small-cell suppression).`
      : null,
  };
}

/**
 * The rule-based one-line takeaway under a chart. Ported from legacy
 * _pptChartTakeaway, made pure: it reads a plain series descriptor rather than
 * reaching into a live Chart instance, so it is unit-testable in node.
 *
 * NO AI, no generation — three deterministic shapes:
 *  - doughnut → the largest segment and its share;
 *  - single-series → the peak point plus the latest point's direction;
 *  - multi-series → NOTHING. A single takeaway over several cohort curves would
 *    describe one line and imply all of them; silence is the honest output.
 *
 * @param {{type?: string, labels?: any[], values?: number[], seriesCount?: number}} desc
 * @returns {string}
 */
export function chartTakeaway(desc) {
  const d = desc || {};
  const labels = Array.isArray(d.labels) ? d.labels : [];
  const values = (Array.isArray(d.values) ? d.values : []).map((v) => (isFinite(Number(v)) ? Number(v) : 0));
  if (!values.length || !values.some((v) => v > 0)) return '';
  if ((d.seriesCount || 1) > 1) return '';
  let mi = 0;
  for (let i = 1; i < values.length; i++) if (values[i] > values[mi]) mi = i;
  const peakLabel = String(labels[mi] === undefined ? mi + 1 : labels[mi]);
  if (d.type === 'doughnut' || d.type === 'pie') {
    const total = values.reduce((a, b) => a + b, 0);
    if (!total) return '';
    return `${peakLabel} is the largest segment (${Math.round((values[mi] / total) * 100)}%).`;
  }
  let dir = '';
  if (values.length > 1) {
    const last = values[values.length - 1];
    const prev = values[values.length - 2];
    dir = last > prev ? 'rising' : last < prev ? 'falling' : 'flat';
  }
  return `Peak: ${peakLabel} (${fmtNum(values[mi])})${dir ? ` · latest point ${dir}` : ''}.`;
}

/**
 * The scope stamp: a human-readable line per active filter. Ported from legacy
 * _pptScopeLines. Every deck says exactly what it is a view OF — a slide deck
 * with no scope stamp is how "enrolment fell" gets quoted from a report that was
 * filtered to one county.
 * @param {any} filters the reports filter state
 * @param {{periodLabel: string, clockLabel: string, programmeLabel?: string|null, sliceLines?: string[]}} ctx
 * @returns {string[]}
 */
export function scopeLines(filters, ctx) {
  const f = filters || {};
  const c = ctx || {};
  const out = [];
  const clock = f.period && f.period !== 'all' && c.clockLabel ? ` · ${String(c.clockLabel).toLowerCase()} clock` : '';
  out.push('Period: ' + (c.periodLabel || 'All time') + clock);
  if (c.programmeLabel) out.push('Programme: ' + c.programmeLabel);
  for (const l of c.sliceLines || []) out.push(l);
  if (f.compare) out.push('Compared against the previous period');
  out.push('Tier: ' + (f.tier === 'analyst' ? 'Analyst (full metric set)' : 'Board (headline set)'));
  return out;
}

/**
 * Render a metric delta for a slide cell. '—' is deliberate and load-bearing:
 * it means "this comparison is not meaningful or not available", which is a
 * different statement from "0%", and the deck must not conflate them.
 * @param {{kind: string, pct: number}|null} d
 * @returns {string}
 */
export function formatDelta(d) {
  if (!d) return '—';
  if (d.kind === 'new') return 'new vs prev';
  const arrow = d.pct > 0 ? '▲ ' : d.pct < 0 ? '▼ ' : '▬ ';
  return arrow + Math.abs(d.pct) + '% vs prev';
}

/** @param {any} res @returns {string} */
function display(res) {
  return res && res.display ? String(res.display) : '—';
}

// ─── The plan builder (PURE) ───────────────────────────────────────────────

/**
 * Build the slide plan: registry results + filters + tile selection → an ordered
 * array of slide descriptors. Deterministic and side-effect free.
 *
 * Slide order (the approved deck shape):
 *   1  cover      — navy, title, subtitle, scope stamp
 *   2  exec       — the SAME rule-based narrative the screen shows + 4 headlines
 *   3  scorecard  — the 12 KPI band as a table, with deltas
 *   4… chart      — one per selected metric that has a live canvas
 *   n… table      — By programme · Why patients leave · County top 10 · Data quality
 *
 * SELECTION: `selection` is the deck-builder checkbox set. Empty means "the
 * whole deck" (the natural reading of ticking nothing); non-empty restricts the
 * chart and table slides to what was ticked. Cover, exec and scorecard are
 * structural and always present — a deck without its scope stamp is unsafe to
 * forward, so it is not optional.
 *
 * @param {{
 *   agg: Record<string, any>,
 *   prev?: Record<string, any>|null,
 *   filters: any,
 *   selection?: string[],
 *   availableCharts?: string[],
 *   chartSeries?: Record<string, {type?: string, labels?: any[], values?: number[], seriesCount?: number}>,
 *   ctx: {
 *     periodLabel: string, clockLabel: string, programmeLabel?: string|null,
 *     sliceLines?: string[], generatedAt: string, generatedBy?: string|null,
 *     clauses?: string[]
 *   }
 * }} input
 * @returns {any[]} slide descriptors
 */
export function buildSlidePlan(input) {
  const inp = input || {};
  const agg = inp.agg || {};
  const prev = inp.prev || null;
  const ctx = inp.ctx || /** @type {any} */ ({});
  const sel = new Set(inp.selection || []);
  const selecting = sel.size > 0;
  const available = new Set(inp.availableCharts || []);
  const series = inp.chartSeries || {};
  /** @param {string} id */
  const wanted = (id) => !selecting || sel.has(id);

  // SMALL-CELL SUPPRESSION IS DECIDED BEFORE ANY SLIDE IS BUILT, for the whole
  // geographic section — not inside the table branch. Suppressing a county in the
  // table while the chart loop ships a PNG of the same ranked series at raw values
  // defeats the guarantee entirely: the deck withheld Lamu on one slide and
  // printed "Lamu 1" on another a few slides earlier. captureCharts snapshots the
  // LIVE canvas, which this module cannot repaint, so a geo metric that has
  // anything to suppress loses its chart slide and keeps the suppressed table as
  // the only geographic cut. A geo metric with no small cells still charts.
  /** @type {Set<string>} */
  const geoSuppressed = new Set();
  for (const m of METRICS) {
    if (!m || m.section !== 'geo') continue;
    const r = agg[m.id];
    const items = (r && r.data && r.data.items) || null;
    if (!Array.isArray(items) || !items.length) continue;
    if (suppressSmallCells(items).hidden > 0) geoSuppressed.add(m.id);
  }

  /** @type {any[]} */
  const slides = [];

  // 1 — Cover.
  slides.push({
    kind: 'cover',
    title: DECK_TITLE,
    subtitle: DECK_SUBTITLE,
    generatedAt: ctx.generatedAt || '',
    generatedBy: ctx.generatedBy || null,
    scopeLines: scopeLines(inp.filters, ctx),
  });

  // 2 — Executive summary: the screen's clauses, verbatim, plus four headline
  // numbers taken straight from registry results (display strings, not re-formats).
  const headlineIds = ['enrolled', 'refills_on_time', 'time_to_treatment', 'serious_ae_mtd'];
  slides.push({
    kind: 'exec',
    title: 'Executive summary',
    headlines: headlineIds.map((id) => {
      const m = getMetric(id) || {};
      const r = agg[id] || {};
      return { id, value: display(r), label: m.label || id, detail: (r && r.sub) || '' };
    }),
    clauses: (ctx.clauses || []).slice(),
  });

  // 3 — KPI scorecard.
  slides.push({
    kind: 'scorecard',
    title: 'KPI scorecard',
    header: ['KPI', 'Value', 'Δ'],
    rows: kpiMetrics().map((m) => {
      const r = agg[m.id] || {};
      const d = prev ? metricDelta(m.id, r.value, prev[m.id] && prev[m.id].value) : null;
      return [m.label, display(r), formatDelta(d)];
    }),
  });

  // 4 — Chart slides. Only metrics that HAVE a rendered canvas: a Board-tier
  // deck must not carry an empty frame where an Analyst tile would have been.
  for (const m of deckChartMetrics()) {
    if (!wanted(m.id) || !available.has(m.id)) continue;
    if (geoSuppressed.has(m.id)) continue; // the chart would un-suppress the table
    const r = agg[m.id] || {};
    slides.push({
      kind: 'chart',
      metricId: m.id,
      title: m.label,
      takeaway: chartTakeaway(Object.assign({ type: m.chart }, series[m.id] || {})),
      note: (r && r.sub) || '',
      // The chart's category LABELS, carried on the descriptor so the privacy
      // assertion can see them. The PNG itself is passed separately as
      // opts.images and is opaque to findIdentifierLeaks — without this, the
      // ~13-15 largest artefacts on the deck sat outside the guarantee, and
      // several charted metrics group by free-text operator-typed columns
      // (patients_by_county keys on `county`). Scanning the series is the
      // closest we can get to scanning the pixels.
      labels: ((series[m.id] || {}).labels || []).map((l) => String(l)),
    });
  }

  // 5 — Native table slides, each off ONE registry result.
  const pdRows = (agg.programme_breakdown && agg.programme_breakdown.data && agg.programme_breakdown.data.rows) || [];
  if (wanted('programme_breakdown') && pdRows.length) {
    const shown = pdRows.slice(0, 12);
    slides.push({
      kind: 'table',
      id: 'programme_breakdown',
      title: 'By programme',
      header: ['Programme', 'Pending', 'Active', 'Closed', 'Archived', 'Serious AEs', 'AEs unsent'],
      colW: [2.7, 1.1, 1.1, 1.1, 1.1, 1.1, 1.1],
      align: ['left', 'right', 'right', 'right', 'right', 'right', 'right'],
      rows: shown.map((r) => [
        String(r.display_name || '—'),
        fmtNum(r.pending_consents),
        fmtNum(r.active_cases),
        fmtNum(r.closed_cases),
        fmtNum(r.archived_cases),
        fmtNum(r.serious_aes_this_month),
        fmtNum(r.aes_pending_email),
      ]),
      note: pdRows.length > 12 ? `Top 12 of ${fmtNum(pdRows.length)} programmes.` : null,
    });
  }

  const whyItems = (agg.closure_reasons && agg.closure_reasons.data && agg.closure_reasons.data.items) || [];
  if (wanted('closure_reasons') && whyItems.length) {
    // 13 rows is the ceiling: 16 archive reasons at 0.3in would overflow a
    // 5.625in slide, and a table that runs off the slide is data lost silently.
    const shown = whyItems.slice(0, 13);
    slides.push({
      kind: 'table',
      id: 'closure_reasons',
      title: 'Why patients leave',
      header: ['Reason', 'Patients'],
      colW: [7.3, 2],
      align: ['left', 'right'],
      rows: shown.map((i) => [String(i.l), fmtNum(i.v)]),
      note: whyItems.length > 13 ? `Top 13 of ${fmtNum(whyItems.length)} reasons.` : null,
    });
  }

  const ctyItems = (agg.patients_by_county && agg.patients_by_county.data && agg.patients_by_county.data.items) || [];
  if (wanted('patients_by_county') && ctyItems.length) {
    // SMALL-CELL SUPPRESSION applies HERE and to every geographic breakdown.
    const guarded = suppressSmallCells(ctyItems.slice(0, 10));
    if (guarded.items.length) {
      slides.push({
        kind: 'table',
        id: 'patients_by_county',
        title: 'Enrolment by county — top 10',
        header: ['County', 'Patients'],
        colW: [7.3, 2],
        align: ['left', 'right'],
        rows: guarded.items.map((i) => [String(i.l), fmtNum(i.v)]),
        note: [
          ctyItems.length > 10 ? `Top 10 of ${fmtNum(ctyItems.length)} counties.` : null,
          guarded.note,
        ]
          .filter(Boolean)
          .join(' '),
        suppressed: guarded.hidden,
      });
    }
  }

  const dqItems = (agg.field_completeness && agg.field_completeness.data && agg.field_completeness.data.items) || [];
  if (wanted('field_completeness') && dqItems.length) {
    const n = agg.field_completeness && agg.field_completeness.n;
    slides.push({
      kind: 'table',
      id: 'field_completeness',
      title: 'Data quality — field completeness',
      header: ['Field', 'Complete'],
      colW: [7.3, 2],
      align: ['left', 'right'],
      rows: dqItems.map((i) => [String(i.l), i.v + '%']),
      note: typeof n === 'number' ? `Across ${fmtNum(n)} cases in the current view.` : null,
    });
  }

  return slides;
}

/**
 * The export file name. Date-stamped so two decks pulled a week apart never
 * overwrite each other in a downloads folder.
 * @param {Date} [now]
 * @returns {string}
 */
export function deckFileName(now) {
  const d = now instanceof Date && !isNaN(now.getTime()) ? now : new Date();
  const stamp =
    d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  return 'Tumaini_Programme_Report_' + stamp + '.pptx';
}

// ─── The privacy assertion (pure; run in tests AND before every export) ────

/**
 * Walk a finished plan for anything that identifies a person. Returns a list of
 * human-readable violations — EMPTY means the plan is safe to write.
 *
 * Two independent checks, because either alone is defeatable:
 *  - STRUCTURAL: no descriptor object anywhere carries an IDENTIFIER_KEYS key.
 *    Catches "we put raw case rows on a slide" before anyone reads the output.
 *  - VALUE: no rendered cell string contains a real identifier VALUE from the
 *    dataset the plan was built over. Catches a name that arrived through a
 *    label — e.g. a county column that turned out to hold a patient's name, or a
 *    future metric that groups by something personal.
 *
 * SCOPE, stated plainly so this is not read as broader than it is: the check sees
 * the PLAN, not the rendered PNGs. Chart slides carry their category `labels` on
 * the descriptor precisely so the series is scannable, but anything a chart
 * library paints that is not in those labels (a datalabel, a tooltip baked into
 * the image, an axis title) is invisible here. Pass the widest case set you have
 * as `dataset` — needles built from a FILTERED set would miss an identifier
 * belonging to a patient the current filter excluded.
 *
 * @param {any[]} plan
 * @param {{cases?: any[]}} [dataset] the rows the plan was built from
 * @returns {string[]} violations (empty = safe)
 */
export function findIdentifierLeaks(plan, dataset) {
  /** @type {string[]} */
  const bad = [];
  const slides = Array.isArray(plan) ? plan : [];

  // 1 — structural: identifier-shaped keys.
  const seen = new Set();
  /** @param {any} node @param {string} path */
  const walk = (node, path) => {
    if (!node || typeof node !== 'object') return;
    if (seen.has(node)) return;
    seen.add(node);
    if (Array.isArray(node)) {
      node.forEach((v, i) => walk(v, path + '[' + i + ']'));
      return;
    }
    for (const k of Object.keys(node)) {
      if (IDENTIFIER_KEYS.includes(k)) bad.push(`identifier key "${k}" at ${path}`);
      walk(node[k], path + '.' + k);
    }
  };
  slides.forEach((s, i) => walk(s, 'slide[' + i + ']' + (s && s.kind ? ':' + s.kind : '')));

  // 2 — value: real identifiers from the dataset appearing in any cell.
  const cases = (dataset && dataset.cases) || [];
  if (cases.length) {
    /** @type {Array<{v: string, k: string}>} */
    const needles = [];
    for (const c of cases) {
      for (const k of ['patient_name', 'reference_number', 'patient_phone', 'national_id']) {
        const v = c && c[k];
        // 4 characters is the floor: shorter strings collide with legitimate
        // labels (a county called "Kwale" vs a reference "KW"), and a false
        // positive that cries wolf gets the whole assertion switched off.
        if (typeof v === 'string' && v.trim().length >= 4) needles.push({ v: v.trim().toLowerCase(), k });
      }
    }
    if (needles.length) {
      /** @type {string[]} */
      const strings = [];
      const seen2 = new Set();
      /** @param {any} node */
      const collect = (node) => {
        if (typeof node === 'string') {
          strings.push(node.toLowerCase());
          return;
        }
        if (!node || typeof node !== 'object' || seen2.has(node)) return;
        seen2.add(node);
        for (const k of Object.keys(node)) collect(node[k]);
      };
      collect(slides);
      const hay = strings.join(' ');
      for (const n of needles) {
        if (hay.includes(n.v)) {
          bad.push(`patient ${n.k} value appears on a slide`);
          break; // one report is enough; the export aborts either way
        }
      }
    }
  }

  return bad;
}

// ─── The renderer (IMPURE — pptxgenjs) ─────────────────────────────────────

/** The vendored pptxgenjs global, or null when the script did not load. @returns {any} */
export function getPptxGen() {
  const P = /** @type {any} */ (globalThis).PptxGenJS;
  return typeof P === 'function' ? P : null;
}

/**
 * Turn a slide plan into a pptxgenjs presentation. Returns the instance — the
 * CALLER writes it (writeFile in the browser, write({outputType}) in the node
 * smoke test), which is what lets the deck path be exercised without a browser.
 *
 * REFUSES TO BUILD a plan that fails the privacy assertion. This is not
 * defence-in-depth theatre: the assertion runs against the live dataset at
 * export time, so a metric that starts leaking names in production is caught on
 * the user's machine, not only in CI.
 *
 * @param {any[]} plan from buildSlidePlan
 * @param {{
 *   PptxGenJS?: any,
 *   images?: Record<string, {dataUrl: string, w: number, h: number}>,
 *   author?: string|null,
 *   dataset?: {cases?: any[]}
 * }} [opts]
 * @returns {any} the pptxgenjs instance
 */
export function renderDeck(plan, opts) {
  const o = opts || {};
  const Ctor = o.PptxGenJS || getPptxGen();
  if (!Ctor) throw new Error('PowerPoint export unavailable — the deck script did not load');

  const leaks = findIdentifierLeaks(plan, o.dataset);
  if (leaks.length) throw new Error('Deck blocked — it would carry patient identifiers: ' + leaks.join('; '));

  const images = o.images || {};
  const pptx = new Ctor();
  pptx.layout = 'LAYOUT_16x9'; // 10in × 5.625in
  pptx.author = o.author || 'Tumaini Haven';
  pptx.title = DECK_TITLE;

  // The master carries the footer + slide number on EVERY slide, including the
  // cover — a slide that gets screenshotted out of the deck keeps its provenance.
  pptx.defineSlideMaster({
    title: 'TUM',
    background: { color: PPT.WHITE },
    objects: [
      { rect: { x: 0, y: 5.28, w: 10, h: 0.008, fill: { color: PPT.LINE } } },
      { text: { text: DECK_FOOTER, options: { x: 0.35, y: 5.32, w: 6.5, h: 0.25, fontSize: 8, color: PPT.MUTED } } },
    ],
    slideNumber: { x: 9.35, y: 5.32, w: 0.5, h: 0.25, fontSize: 8, color: PPT.MUTED },
  });

  /** @param {any} s @param {string} t */
  const addTitle = (s, t) =>
    s.addText(t, { x: 0.35, y: 0.22, w: 9.3, h: 0.5, fontSize: 20, bold: true, color: PPT.NAVY, fontFace: 'Calibri' });
  /** @param {string} t */
  const th = (t) => ({ text: String(t), options: { bold: true, color: PPT.WHITE, fill: { color: PPT.NAVY } } });
  /** @param {any} t @param {number} i @param {string} [align] */
  const td = (t, i, align) => ({
    text: String(t === null || t === undefined ? '—' : t),
    options: Object.assign({ fill: { color: i % 2 ? PPT.ALT : PPT.WHITE }, valign: 'middle' }, align === 'right' ? { align: 'right', bold: true } : {}),
  });

  for (const sl of plan || []) {
    if (sl.kind === 'cover') {
      const s = pptx.addSlide({ masterName: 'TUM' });
      s.background = { color: PPT.NAVY };
      s.addText(sl.title, { x: 0.6, y: 1.45, w: 8.8, h: 0.9, fontSize: 34, bold: true, color: PPT.WHITE, fontFace: 'Calibri' });
      s.addText(sl.subtitle, { x: 0.6, y: 2.35, w: 8.8, h: 0.4, fontSize: 15, color: PPT.ONNAVY });
      s.addText(
        [
          {
            text: 'Generated ' + sl.generatedAt + (sl.generatedBy ? ' by ' + sl.generatedBy : ''),
            options: { breakLine: true },
          },
        ].concat((sl.scopeLines || []).map((/** @type {string} */ t) => ({ text: t, options: { breakLine: true } }))),
        { x: 0.6, y: 3.1, w: 8.8, h: 1.9, fontSize: 11, color: PPT.ONNAVY, lineSpacing: 16 }
      );
      continue;
    }

    if (sl.kind === 'exec') {
      const s = pptx.addSlide({ masterName: 'TUM' });
      addTitle(s, sl.title);
      (sl.headlines || []).forEach((/** @type {any} */ h, /** @type {number} */ i) => {
        s.addText(
          [
            { text: String(h.value), options: { fontSize: 26, bold: true, color: PPT.NAVY, breakLine: true } },
            { text: String(h.label), options: { fontSize: 10.5, bold: true, color: PPT.INK, breakLine: true } },
            { text: String(h.detail || ''), options: { fontSize: 9, color: PPT.MUTED } },
          ],
          { x: 0.35 + i * 2.37, y: 0.85, w: 2.25, h: 1.35, fill: { color: PPT.TILE }, align: 'center', valign: 'middle' }
        );
      });
      const clauses = sl.clauses || [];
      s.addText(
        clauses.length
          ? clauses.map((/** @type {string} */ c) => ({
              text: c,
              options: { bullet: { code: '2022' }, breakLine: true, fontSize: 12.5, color: PPT.INK },
            }))
          : 'No narrative — no patients in the current view.',
        { x: 0.45, y: 2.45, w: 9.1, h: 2.6, lineSpacing: 20, valign: 'top' }
      );
      continue;
    }

    if (sl.kind === 'scorecard') {
      const s = pptx.addSlide({ masterName: 'TUM' });
      addTitle(s, sl.title);
      const head = [
        th(sl.header[0]),
        { text: sl.header[1], options: { bold: true, color: PPT.WHITE, fill: { color: PPT.NAVY }, align: 'right' } },
        { text: sl.header[2], options: { bold: true, color: PPT.WHITE, fill: { color: PPT.NAVY }, align: 'right' } },
      ];
      const body = (sl.rows || []).map((/** @type {any[]} */ r, /** @type {number} */ i) => [
        td(r[0], i),
        td(r[1], i, 'right'),
        {
          text: String(r[2]),
          options: { align: 'right', color: PPT.MUTED, fill: { color: i % 2 ? PPT.ALT : PPT.WHITE }, valign: 'middle' },
        },
      ]);
      s.addTable([head].concat(body), {
        x: 0.35, y: 0.8, w: 9.3, colW: [5.3, 2, 2], fontSize: 10.5, color: PPT.INK,
        border: { type: 'solid', color: PPT.LINE, pt: 0.5 }, rowH: 0.31, valign: 'middle',
      });
      continue;
    }

    if (sl.kind === 'chart') {
      const img = images[sl.metricId];
      if (!img || !img.dataUrl) continue; // the canvas vanished between plan and render
      const s = pptx.addSlide({ masterName: 'TUM' });
      addTitle(s, sl.title);
      const boxW = 9.3;
      const boxH = sl.takeaway ? 3.85 : 4.15;
      const ratio = img.h && img.w ? img.h / img.w : 0.5;
      let w = boxW;
      let h = w * ratio;
      if (h > boxH) {
        h = boxH;
        w = ratio ? h / ratio : boxW;
      }
      s.addImage({ data: img.dataUrl, x: 0.35 + (boxW - w) / 2, y: 0.85, w, h });
      if (sl.takeaway) {
        s.addText(sl.takeaway, { x: 0.35, y: 4.8, w: 9.3, h: 0.35, fontSize: 11.5, italic: true, color: PPT.MUTED });
      }
      continue;
    }

    if (sl.kind === 'table') {
      const s = pptx.addSlide({ masterName: 'TUM' });
      addTitle(s, sl.title);
      const align = sl.align || [];
      const head = (sl.header || []).map((/** @type {string} */ t, /** @type {number} */ ci) =>
        align[ci] === 'right'
          ? { text: String(t), options: { bold: true, color: PPT.WHITE, fill: { color: PPT.NAVY }, align: 'right' } }
          : th(t)
      );
      const body = (sl.rows || []).map((/** @type {any[]} */ r, /** @type {number} */ i) =>
        r.map((c, ci) => td(c, i, align[ci]))
      );
      s.addTable([head].concat(body), {
        x: 0.35, y: 0.8, w: 9.3, colW: sl.colW, fontSize: 10.5, color: PPT.INK,
        border: { type: 'solid', color: PPT.LINE, pt: 0.5 }, rowH: 0.3, valign: 'middle',
      });
      if (sl.note) {
        s.addText(String(sl.note), { x: 0.35, y: 4.95, w: 9.3, h: 0.3, fontSize: 9.5, italic: true, color: PPT.MUTED });
      }
    }
  }

  return pptx;
}
